/**
 * Prepare — cook once, keep it, eat it over the week, or send someone the recipe.
 *
 * The seventh destination, and the first that is about food *before* it is
 * eaten. Everything else in the bar is about a day (Today, Plan, Review), a
 * body (Weight), a person (You) or the rows logging uses (Foods). Cooking a pot
 * is none of those: it is a statement about a pan, made once, that the diary
 * then draws on for days. It used to live as four modes inside the add sheet —
 * reached only by opening a meal you were about to log, on the one surface in
 * the app built for writing instantly — and a pot half-built there died with
 * the sheet on Escape. A pot deserves an address.
 *
 * ## Vocabulary
 *
 * The code says `pot` and `batch`, which describe the object: a cooked batch,
 * divided into portions. The screen says **Prepare**, which describes the act.
 * The primary action is **Cook a pot**; what is stored is **In the fridge**;
 * the flow is New pot → Divide a pot → Store N portions; and a pot's own page
 * is titled by its name. Nothing here is called "prepared" — the fridge says
 * the state better than the participle does.
 *
 * ## What is deliberately not here
 *
 * - **Saved meals as starting points.** "Cook a pot" opens on them; a second
 *   list on this page would be the same list said twice.
 * - **Every link you have ever made.** There is no cross-pot endpoint for it,
 *   and "1 link live" on each row is the honest one-line answer.
 * - **Finished and thrown-out pots.** The list the server sends is the fridge,
 *   not its history; a pot's page still answers by id after it is gone.
 * - **Eating.** "You add it from the meal logger": the sheet knows the meal and
 *   the day, this page knows neither. Eating lives on the pot's page, where a
 *   meal is asked for first.
 *
 * ## Renaming, by holding the row
 *
 * A pot is named as it goes on the stove, which is the worst moment to name
 * anything: the pan is on, the name is the last thing on anybody's mind, and
 * three rows of "New pot" in the fridge a week later is what that costs. So the
 * name stays editable, from the list, without opening the pot: hold a row and
 * it grows a field. A tap still opens the pot — the two gestures do not compete
 * — and the rename is the pot's own, not its recipe's, so renaming Sunday's
 * chilli leaves Wednesday's pot and the saved meal alone.
 */

import { formatPortions, useBatches, useUpdateBatch, type CookedBatch } from "@calorio/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { urgent } from "../components/pot";
import { useHold } from "../lib/hold";

const day = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export function Prepare() {
  const ctx = useCalorio();
  const batches = useBatches(ctx);
  const pots = batches.data ?? [];
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="stack">
      <header className="plan__head">
        <div>
          <h1 className="plan__title">Prepare</h1>
          <p className="stat__label" style={{ margin: 0 }}>
            Cook once, divide it, and eat it over the week — or send somebody the recipe.
          </p>
        </div>
        <div className="plan__actions">
          <Link to="/prepare/new" className="plan__pill plan__pill--action">
            Cook a pot
          </Link>
        </div>
      </header>

      <section className="prepare">
        <h2 className="card__title">In the fridge</h2>

        {batches.isLoading && <p className="muted">…</p>}

        {batches.data && pots.length === 0 && (
          <div className="card stack" style={{ gap: "var(--sp-2)" }}>
            <p className="manual__note">
              Nothing in the fridge. Cook a pot and it lives here — how many portions are left, when
              to eat them by, and the link you can send.
            </p>
            <p className="manual__note">
              <Link to="/prepare/new" className="muted">
                Cook a pot →
              </Link>
            </p>
          </div>
        )}

        {pots.length > 0 && (
          <ul className="prepare__pots">
            {pots.map((pot) => (
              <li key={pot.id}>
                <PotRow pot={pot} todayIso={todayIso} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PotRow({ pot, todayIso }: { pot: CookedBatch; todayIso: string }) {
  const ctx = useCalorio();
  const update = useUpdateBatch(ctx);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState(pot.recipe_name);
  const hold = useHold(() => {
    setName(pot.recipe_name);
    setNaming(true);
  });

  const share = pot.portions_made > 0 ? pot.portions_remaining / pot.portions_made : 0;
  const isUrgent = urgent(pot, todayIso);

  const meta = [
    `cooked ${day(pot.cooked_date)}`,
    // The same rule as the sheet's tile: a weighed pot has a gram figure, an
    // unweighed one is a fraction, and both say what a portion comes to.
    [
      pot.weighed && pot.portion_grams
        ? `${Math.round(pot.portion_grams)} g`
        : `1 of ${pot.portions_made}`,
      `${Math.round(pot.kcal_per_portion ?? 0)} kcal`,
    ].join(" · "),
    isUrgent ? "eat today" : pot.best_before ? `best before ${day(pot.best_before)}` : null,
    pot.live_shares > 0 ? `${pot.live_shares} link${pot.live_shares === 1 ? "" : "s"} live` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const bar = (
    <>
      <span className="prepare__meta num">{meta}</span>
      <span className="prepare__bar" aria-hidden="true">
        <span style={{ width: `${Math.max(0, Math.min(1, share)) * 100}%` }} />
      </span>
    </>
  );

  const rename = () => {
    setNaming(false);
    const next = name.trim();
    if (next === pot.recipe_name) return;
    // An emptied field is not a pot called nothing: the server reads `""` as
    // "call it whatever the recipe is called", which is where the name came
    // from in the first place.
    update.mutate({ id: pot.id, name: next });
  };

  if (naming) {
    return (
      <form
        className={`prepare__pot prepare__pot--naming${isUrgent ? " is-urgent" : ""}`}
        onSubmit={(event) => {
          event.preventDefault();
          rename();
        }}
      >
        {/* The row's own top line, with the name as a field. The count stays
            beside it and the bar stays under it: the card grows a little, it
            does not become a different card. */}
        <span className="prepare__top">
          <input
            className="prepare__rename"
            value={name}
            autoFocus
            maxLength={80}
            aria-label={`Rename ${pot.recipe_name}`}
            // Selected on arrival, because the name being replaced is usually
            // "New pot" and nobody holds a row to keep that.
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setNaming(false);
            }}
          />
          <span className="prepare__left num">
            {formatPortions(pot.portions_remaining)} of {pot.portions_made} left
          </span>
        </span>
        {bar}
        <span className="prepare__naming-actions">
          <button type="button" className="prepare__naming-cancel" onClick={() => setNaming(false)}>
            Cancel
          </button>
          <button type="submit" className="prepare__naming-save">
            Save
          </button>
        </span>
      </form>
    );
  }

  return (
    <Link
      to={`/prepare/${pot.id}`}
      className={`prepare__pot${isUrgent ? " is-urgent" : ""}`}
      {...hold.handlers}
      onClick={(event) => {
        // The release at the end of a hold. React Router navigates unless the
        // click is defaulted away, and the field is already open above it.
        if (hold.held.current) {
          hold.held.current = false;
          event.preventDefault();
        }
      }}
    >
      <span className="prepare__top">
        <span className="prepare__name">{pot.recipe_name}</span>
        <span className="prepare__left num">
          {formatPortions(pot.portions_remaining)} of {pot.portions_made} left
        </span>
      </span>
      {bar}
    </Link>
  );
}
